<template>
  <v-app :dir="appDir">
    <NavBar
      :themeToggle="themeToggle"
      :isDark="isDark"
      :langToggle="langToggle"
      :languageOptions="languageOptions"
      :keepAliveStatusColor="keepAliveStatusColor"
      :lastKeepAliveTime="lastKeepAliveTime"
      :lastKeepAliveDisplay="lastKeepAliveDisplay"
      :isCapenaConnected="isCapenaConnected"
      @toggle-theme="toggleTheme"
      @set-locale="setLocale"
    />
    <v-main class="main-bg">
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import NavBar from './components/NavBar.vue';
export default {
  data() {
    return {
      themeToggle: this.isDark ? "dark" : "light",
      langToggle: this.$i18n.locale === "he" ? "he" : "en",
      keepAliveTimer: null,
      now: Date.now(),
      languageOptions: [
        { value: "he", label: "עב", flag: "il" },
        { value: "en", label: "EN", flag: "us" },
      ],
    };
  },
  computed: {
    ...mapGetters([
      "isWebSocketConnected",
      "websocketStatus",
      "lastKeepAliveTime",
      "isAuthenticated",
    ]),
    isDark() {
      return this.$vuetify.theme.global.name === "dark";
    },
    appDir() {
      return this.$i18n.locale === "he" ? "rtl" : "ltr";
    },
    lastKeepAliveDisplay() {
      if (!this.lastKeepAliveTime) return null;
      const diff = Math.floor(
        (this.now - new Date(this.lastKeepAliveTime)) / 1000
      );
      if (diff < 0) return null;
      if (diff < 60) {
        return `${diff} ${this.$t("seconds")}`;
      } else if (diff < 3600) {
        const mins = Math.floor(diff / 60);
        return `${mins} ${this.$t("minutes")}`;
      } else if (diff < 86400) {
        const hours = Math.floor(diff / 3600);
        return `${hours} ${this.$t("hours")}`;
      } else {
        const days = Math.floor(diff / 86400);
        return `${days} ${this.$t("days")}`;
      }
    },
    keepAliveStatusColor() {
      return this.isCapenaConnected ? "success" : "error";
    },
    isCapenaConnected() {
      if (!this.lastKeepAliveTime) return false;
      const diff = Math.floor(
        (this.now - new Date(this.lastKeepAliveTime)) / 1000
      );
      return diff <= 60;
    }
  },
  mounted() {
    // Load cache ttl config
    this.fetchUiTtl();

    // Update timer for keep-alive display
    this.keepAliveTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },

  beforeUnmount() {
    // Stop statistics monitoring
    this.stopStatisticsMonitoring();

    // Clean up WebSocket connection
    this.disconnectWebSocket();
    if (this.keepAliveTimer) clearInterval(this.keepAliveTimer);
  },
  watch: {
    appDir: {
      immediate: true,
      handler(val) {
        document.documentElement.setAttribute("dir", val);
      },
    },
    "$i18n.locale"(val) {
      this.langToggle = val;
    },
    isDark(val) {
      this.themeToggle = val ? "dark" : "light";
    },
  },
  methods: {
    ...mapActions([
      "loadAlertsFromCache",
      "startStatisticsMonitoring",
      "connectWebSocket",
      "stopStatisticsMonitoring",
      "disconnectWebSocket",
      "fetchUiTtl",
      "loadLastKeepAliveFromCache",
    ]),
    toggleTheme(mode) {
      this.$vuetify.theme.change(mode);
    },
    setLocale(lang) {
      if (this.$i18n.locale !== lang) {
        this.$i18n.locale = lang;
        this.$vuetify.locale.current = lang;
        this.$vuetify.locale.rtl = lang === "he";
        document.documentElement.setAttribute(
          "dir",
          lang === "he" ? "rtl" : "ltr"
        );
      }
    },
  },
  components: {
    NavBar,
  },
};
</script>

<style scoped>
.main-bg {
  background: linear-gradient(
    135deg,
    var(--v-theme-background) 0%,
    var(--v-theme-surface) 100%
  );
  height: calc(100vh - 64px);
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 100vw;
  width: 100vw;
}
.modern-navbar {
  background: linear-gradient(
    90deg,
    var(--v-theme-surface) 0%,
    var(--v-theme-background) 100%
  );
  border-radius: 0 0 18px 18px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  min-height: 64px;
  transition: background 0.3s;
}
.v-theme--dark .modern-navbar {
  background: linear-gradient(90deg, #23272f 0%, #181c22 100%) !important;
}
.navbar-row {
  width: 100%;
  display: flex;
  align-items: center;
}
.flex-row-reverse {
  flex-direction: row-reverse !important;
}
.flex-row {
  flex-direction: row !important;
}
.toolbar-title-col {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}
.toolbar-btns-col {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}
.modern-title {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: var(--v-theme-on-surface);
  padding-inline: 24px;
  text-transform: uppercase;
  background: none;
}

/* Improve visibility in dark mode */
.v-theme--dark .modern-title {
  color: #ffffff !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}
.modern-toggle {
  background: rgba(255, 255, 255, 0.13);
  border-radius: 24px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  gap: 8px;
}
.modern-toggle-btn,
.lang-toggle-btn {
  min-width: 110px;
  max-width: 110px;
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 14px;
  background: transparent;
  border-radius: 20px !important;
  font-size: 1.05em;
  font-weight: 600;
  letter-spacing: 1px;
}
.modern-toggle-btn.active,
.modern-toggle-btn.v-btn--active {
  background: var(--v-theme-secondary) !important;
  color: var(--v-theme-on-secondary) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.toggle-label {
  font-size: 0.85rem;
  margin-top: 2px;
  color: inherit;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 500;
}
.history-btn {
  border-radius: 20px;
  min-width: 80px;
  min-height: 40px;
  padding: 0 16px;
  background: transparent;
  color: var(--v-theme-primary);
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
  margin-inline-start: 8px;
}
.history-btn:hover {
  background: var(--v-theme-secondary);
  color: var(--v-theme-on-secondary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.btn-label {
  font-size: 0.75rem;
  margin-top: 2px;
  color: inherit;
  letter-spacing: 1px;
  text-transform: uppercase;
}

html {
  overflow: hidden !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

html::-webkit-scrollbar {
  width: 0;
  height: 0;
}
.settings-btn {
  margin-inline-end: 8px;
}
.settings-btn:focus,
.settings-btn:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}
.settings-menu-card {
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}
.settings-section {
  margin-bottom: 8px;
}
.settings-label {
  font-size: 1.1em;
  font-weight: 700;
  color: var(--v-theme-on-surface);
  margin-bottom: 4px;
  margin-top: 2px;
  letter-spacing: 0.5px;
}
.lang-btn-toggle {
  display: flex;
}
.lang-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 1.05em;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 0 16px;
  border-radius: 20px !important;
}
.lang-flag {
  display: flex;
  align-items: center;
}
.lang-short-label {
  font-size: 1.05em;
  font-weight: 700;
  color: var(--v-theme-primary);
  letter-spacing: 0.5px;
}
@media (max-width: 768px) {
  .modern-navbar {
    width: 100vw !important;
    min-width: 100vw !important;
    margin: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    left: 0 !important;
    right: 0 !important;
  }
  .navbar-row {
    width: 100vw !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .toolbar-title-col, .toolbar-btns-col {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin: 0 !important;
  }
}
</style>
